import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import Image from 'next/image';
import type { ImageShape, ImageWidgetResolved } from './types';
import { Link } from '@/components/Link';
import type { EventTrackingResolved } from '@/lib/gtm/pushCmsEventTracking';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { imageAlt } from '@/lib/render/imageAlt';
import {
  useOptionalRenderContext,
  useSubstitute,
} from '@/lib/render/RenderContextProvider';
import { substituteHref } from '@/lib/render/substituteHref';
import { Text } from '@/lib/render/Text';

const WRAPPER_ALIGNMENTS: Record<
  NonNullable<ImageWidgetResolved['alignment']>,
  string
> = {
  'left': 'mr-auto max-w-3xl',
  'center': 'mx-auto max-w-3xl',
  'right': 'ml-auto max-w-3xl',
  'full-width': 'w-full',
};

/*
 * Matches the alignments above, inside the container the SlotRenderer wrapper
 * now supplies (70.14-Widget-Width).
 *
 * `full-width` means the **full content width**, not the viewport: `w-full` is
 * 100% of the parent, and the parent is the page container. That is what it
 * already rendered — nothing ever let an image escape the gutter — so the
 * migration keeps that reading rather than changing behaviour. Genuinely
 * edge-to-edge would be a new mechanism, not this alignment (70.14 § Migration).
 *
 * Narrow images are now the full 768px of `max-w-3xl`: the figure no longer
 * carries `px-4` of its own, so they no longer lose 32px to it. The mobile
 * expression is unchanged — the gutter simply moved up to the wrapper.
 */
const NARROW_SIZES = '(max-width: 800px) calc(100vw - 2rem), 768px';
const CONTENT_SIZES = '(max-width: 1152px) calc(100vw - 2rem), 1080px';
const SIZES: Record<NonNullable<ImageWidgetResolved['alignment']>, string> = {
  'left': NARROW_SIZES,
  'center': NARROW_SIZES,
  'right': NARROW_SIZES,
  'full-width': CONTENT_SIZES,
};

/*
 * A second picture splits the row, so each is served at roughly half the width
 * — the single-picture values above would have the optimizer fetch twice the
 * pixels needed. The `gap-4` (16px) comes off the pair before halving:
 * (768 − 16) / 2 = 376px, and the mobile expression loses half the gap on each
 * side.
 */
const NARROW_PAIR_SIZES = '(max-width: 800px) calc(50vw - 1.5rem), 376px';
const PAIR_SIZES: Record<
  NonNullable<ImageWidgetResolved['alignment']>,
  string
> = {
  'left': NARROW_PAIR_SIZES,
  'center': NARROW_PAIR_SIZES,
  'right': NARROW_PAIR_SIZES,
  'full-width': '(max-width: 1152px) calc(50vw - 1.5rem), 532px',
};

/*
 * A block placed before `imageShape` existed carries no value, and must keep
 * the square corners it renders with today — so the fallback is
 * `non-rounded`, not the schema's `rounded` default for *new* blocks. The
 * migration `20261009090000-image-shape-non-rounded` writes the same value
 * into those rows; this is what keeps them right until it has run.
 */
const DEFAULT_SHAPE: ImageShape = 'non-rounded';

/** Corner treatment of a picture rendered at its own aspect ratio. */
const SHAPE_CLASSES: Record<ImageShape, string> = {
  'non-rounded': '',
  // The same radius the Flexible Content Card gives its picture.
  'rounded': 'rounded-2xl',
  // `circle` is not a radius on the picture — it is a square crop, below.
  'circle': '',
};

/*
 * The circle is capped at the 276px of the Teachers widget's avatar
 * (`TeacherCard.tsx`), the established portrait size on this site: an
 * uncapped one would be a 736px disc at `center` alignment, which no
 * portrait wants. The cap also pins the rendered width, so the optimizer is
 * told 276px rather than the content measure.
 */
const CIRCLE_SIZES = '276px';
const CIRCLE_PAIR_SIZES = '(max-width: 800px) calc(50vw - 1.5rem), 276px';

/*
 * Narrower than its slot, the circle needs the alignment the wrapper can no
 * longer express on its own: `max-w-3xl` plus `mx-auto` would centre a 276px
 * disc inside a 736px figure even at `left`.
 */
const CIRCLE_ALIGNMENTS: Record<
  NonNullable<ImageWidgetResolved['alignment']>,
  string
> = {
  'left': 'mr-auto',
  'center': 'mx-auto',
  'right': 'ml-auto',
  // A disc cannot span the viewport; centre it as the next best thing.
  'full-width': 'mx-auto',
};

/** Fallback square for a circle whose media row has no intrinsic dimensions. */
const CIRCLE_FALLBACK_SIZE = 552;

interface PictureProps {
  url: string;
  /** Already substituted — editor-typed alt text may carry a placeholder. */
  alt: string;
  /** Intrinsic dimensions, when Strapi has them for this media row. */
  width: number | undefined;
  height: number | undefined;
  sizes: string;
  shape: ImageShape;
  /** Auto-margins for the circle, which is narrower than its slot. */
  circleAlignment: string;
  href: string;
  target: ImageWidgetResolved['target'];
  tracking: EventTrackingResolved[] | undefined;
  /** Read as the link's accessible name when the picture is decorative. */
  caption: string | undefined;
}

/**
 * One picture, linked when it has a target of its own.
 *
 * Extracted so both pictures are built the same way: each carries its own
 * `href`/`target`/`tracking` (SH-323), rather than one link wrapping the pair —
 * they sit side by side but are separate destinations.
 */
function Picture({
  url,
  alt,
  width,
  height,
  sizes,
  shape,
  circleAlignment,
  href,
  target,
  tracking,
  caption,
}: PictureProps) {
  // The two branches below the circle exist because Strapi does not guarantee
  // intrinsic dimensions on every media row, and next/image needs either both
  // or `fill` — so a row without them falls back to a 16:9 `fill` box rather
  // than dropping the picture.
  const picture =
    shape === 'circle' ? (
      // Portrait/avatar treatment: a square box that crops the picture to a
      // circle. `object-cover` fills it whatever the upload's aspect ratio, so
      // this branch does not need the intrinsic dimensions to be right — only
      // present, which the fallback guarantees.
      <div
        className={classNames(
          'aspect-square w-full max-w-[276px] overflow-hidden rounded-full',
          circleAlignment,
        )}
      >
        <Image
          src={url}
          alt={alt}
          width={width ?? CIRCLE_FALLBACK_SIZE}
          height={height ?? CIRCLE_FALLBACK_SIZE}
          sizes={sizes}
          className="h-full w-full object-cover"
        />
      </div>
    ) : width && height ? (
      <Image
        src={url}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        className={classNames('h-auto w-full', SHAPE_CLASSES[shape])}
      />
    ) : (
      <div className="relative aspect-video w-full">
        <Image
          src={url}
          alt={alt}
          fill
          sizes={sizes}
          className={classNames('object-contain', SHAPE_CLASSES[shape])}
        />
      </div>
    );

  if (!href) {
    return picture;
  }
  return (
    <Link
      href={href}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      // The alt text is the link's accessible name. When the picture is
      // decorative (no alt), fall back to the caption so the link is not
      // announced as a bare URL.
      aria-label={alt ? undefined : caption}
      className="block"
      onClick={() =>
        pushCmsEventTracking(tracking, {
          label: alt || href,
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        })
      }
    >
      {picture}
    </Link>
  );
}

export function ImageWidgetComponent({
  resolved,
}: {
  resolved: ImageWidgetResolved;
}) {
  const {
    image,
    altText = '',
    secondImage,
    secondAltText = '',
    caption,
    alignment = 'center',
    imageShape,
    href: rawHref,
    target,
    tracking,
    secondHref: rawSecondHref,
    secondTarget,
    secondTracking,
  } = resolved;
  const ctx = useOptionalRenderContext();
  const sub = useSubstitute();
  const url = ctx ? ctx.imageUrl(image) : (image?.url ?? '');
  const secondUrl = secondImage
    ? ctx
      ? ctx.imageUrl(secondImage)
      : (secondImage.url ?? '')
    : '';
  if (!url) {
    return null;
  }
  const wrapperAlignment =
    WRAPPER_ALIGNMENTS[alignment] ?? WRAPPER_ALIGNMENTS.center;
  // An unresolved placeholder or a whitespace-only field must not produce a
  // link — a clickable picture that goes nowhere is worse than a plain one.
  const href = substituteHref(rawHref, sub);
  const secondHref = substituteHref(rawSecondHref, sub);
  // Editor-typed, so they may carry a placeholder a screen reader would read
  // out verbatim. The block's own `altText` is a per-placement override; with
  // none set, the media library's alt text names the picture.
  const alt = sub(altText || imageAlt(image));
  const secondAlt = sub(secondAltText || imageAlt(secondImage));
  // Validated rather than defaulted: the backend passes the column through
  // verbatim, so this sees `null` on a row the backfill has not reached, and
  // a stale value on one whose enum entry was renamed. Both mean "the shape
  // this block rendered before anyone could choose one".
  const shape: ImageShape =
    imageShape && imageShape in SHAPE_CLASSES ? imageShape : DEFAULT_SHAPE;
  const circleAlignment =
    CIRCLE_ALIGNMENTS[alignment] ?? CIRCLE_ALIGNMENTS.center;
  const sizes =
    shape === 'circle'
      ? secondUrl
        ? CIRCLE_PAIR_SIZES
        : CIRCLE_SIZES
      : secondUrl
        ? (PAIR_SIZES[alignment] ?? NARROW_PAIR_SIZES)
        : (SIZES[alignment] ?? NARROW_SIZES);

  const first = (
    <Picture
      url={url}
      alt={alt}
      width={image?.width}
      height={image?.height}
      sizes={sizes}
      shape={shape}
      circleAlignment={circleAlignment}
      href={href}
      target={target}
      tracking={tracking}
      caption={caption}
    />
  );

  // `pt-4` only: the gutter comes from the SlotRenderer wrapper now, and
  // `wrapperAlignment` is a measure inside it (70.14-Widget-Width).
  return (
    <figure className={classNames('pt-4', wrapperAlignment)}>
      {/*
        Two pictures share the row when a second one is set, otherwise the
        single one keeps the full width it always had. A grid rather than two
        stacked blocks because this block is placed *inside* a Two Column
        Layout column (SH-323) — the layout is already one level up and cannot
        arrange them.

        `grid-cols-2` at every width on purpose: the pair is a unit, and
        stacking them on mobile would read as two separate pictures.
      */}
      {secondUrl ? (
        <div className="grid grid-cols-2 items-start gap-4">
          {first}
          <Picture
            url={secondUrl}
            alt={secondAlt}
            width={secondImage?.width}
            height={secondImage?.height}
            sizes={sizes}
            shape={shape}
            circleAlignment={circleAlignment}
            href={secondHref}
            target={secondTarget}
            tracking={secondTracking}
            caption={caption}
          />
        </div>
      ) : (
        first
      )}
      {caption && (
        <Text as="span" className="mt-2 block text-sm text-gray-500">
          {caption}
        </Text>
      )}
    </figure>
  );
}
